/**
 * Canonical deal totals calculation.
 *
 * This is the single source of truth for aggregating product-level numbers
 * into deal-level totals. All consumers (frontend components, backend services)
 * should use these functions.
 */
import { toNumber, round2 } from './numeric'
import { monthsFor } from './months'

export interface DealTotals {
  grossProfit: number
  monthlyTotal: number
  mrr: number
  serviceMrr: number
  setupFee: number
  spend: number
  implementationFee: number
  total: number
  totalGrossProfit: number
}

export interface ProductForTotals {
  startDate?: string
  endDate?: string
  qty?: unknown
  phase?: string
  price?: unknown
  mediaSpend?: unknown
  grossProfit?: unknown
  billedPrice?: unknown
  payer?: string
  is_mrr?: boolean
  is_service_mrr?: boolean
  points?: unknown
  ratePerPoint?: unknown
}

export interface DealForTotals {
  implementationFee?: unknown
  mrr?: unknown
  serviceMrr?: unknown
  type?: string
  numberOfPayments?: number
  billingModel?: 'points' | 'legacy'
  ratePerPoint?: number
}

/**
 * Calculate totals for a deal based on its products.
 */
export const calculateDealTotals = (
  deal: DealForTotals | undefined,
  products: ProductForTotals[]
): DealTotals => {
  const sums = (products ?? []).reduce(
    (acc, row) => {
      const months = monthsFor(row.startDate, row.endDate) || toNumber(row.qty) || 1
      const isSetup = row.phase === 'setup'
      const rawPrice = toNumber(row.price)
      const pointsPrice = deal?.billingModel === 'points'
        ? toNumber(row.points) * toNumber(row.ratePerPoint) : 0
      const price = pointsPrice > 0 ? pointsPrice : rawPrice
      const mediaSpend = toNumber(row.mediaSpend)
      const gpMonthly = toNumber(row.grossProfit) || (price - mediaSpend)
      const billedMonthly = row.payer === 'Client' ? toNumber(row.billedPrice) || gpMonthly : price

      if (isSetup) {
        acc.setupFee += billedMonthly * months
      } else {
        acc.monthlyTotal += billedMonthly
        acc.grossProfitMonthly += gpMonthly
        if (row.is_mrr) acc.mrr += billedMonthly
        if (row.is_service_mrr) acc.serviceMrr += billedMonthly
      }
      acc.spend += mediaSpend
      acc.total += billedMonthly * months
      acc.totalGrossProfit += gpMonthly * months

      return acc
    },
    { monthlyTotal: 0, spend: 0, grossProfitMonthly: 0, total: 0, totalGrossProfit: 0, setupFee: 0, mrr: 0, serviceMrr: 0 }
  )

  const implementationFee = toNumber(deal?.implementationFee)

  const dealMrr = sums.mrr > 0 ? sums.mrr : toNumber(deal?.mrr)
  const dealServiceMrr = sums.serviceMrr > 0 ? sums.serviceMrr : toNumber(deal?.serviceMrr)

  return {
    grossProfit: round2(sums.grossProfitMonthly),
    monthlyTotal: round2(sums.monthlyTotal),
    mrr: round2(dealMrr),
    serviceMrr: round2(dealServiceMrr),
    setupFee: round2(sums.setupFee),
    spend: round2(sums.spend),
    implementationFee: round2(implementationFee),
    total: round2(sums.total + implementationFee),
    totalGrossProfit: round2(sums.totalGrossProfit + implementationFee),
  }
}

/**
 * Calculate totals including payment amount for CSA/RCSA deals.
 */
export const calculateDealTotalsWithPayments = (
  deal: DealForTotals | undefined,
  products: ProductForTotals[]
): DealTotals & { paymentAmount: number } => {
  const totals = calculateDealTotals(deal, products)

  const dealType = String(deal?.type ?? '').toUpperCase()
  const isCsa = dealType === 'CSA' || dealType === 'RCSA'

  const numberOfPayments = isCsa ? Math.max(1, toNumber(deal?.numberOfPayments)) : 1
  const paymentAmount = numberOfPayments > 0 ? round2(totals.total / numberOfPayments) : totals.total

  return { ...totals, paymentAmount }
}

/**
 * Calculate appropriate number of payments for CSA deals.
 * Rule: If total < $2501, use 1 payment; otherwise use deal's numberOfPayments or default to 2.
 */
export const calculateNumberOfPayments = (deal: DealForTotals, totals?: DealTotals): number => {
  if (!['CSA', 'RCSA'].includes(deal?.type ?? '')) return 1

  const total = totals?.total ?? 0
  if (total < 2501) return 1

  return deal?.numberOfPayments ?? 2
}
